import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";
import { paymentMethods } from "@/data/payments";
import { paymentPath } from "@/lib/format";

export function PaymentsHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const title = isSv
    ? "Betalningsmetoder hos casinon — komplett guide 2026"
    : "Casino payment methods — complete 2026 guide";
  const desc = isSv
    ? "Översikt över alla vanliga betalningsmetoder hos svenska casinon: Swish, Trustly, Zimpler, BankID, kort, e-plånböcker och krypto."
    : "Overview of common payment methods at Swedish casinos: Swish, Trustly, Zimpler, BankID, cards, e-wallets and crypto.";

  const groups = {
    sv: {
      mobile: "Mobilbetalningar",
      bank: "Banköverföring",
      card: "Kort",
      ewallet: "E-plånbok",
      crypto: "Kryptovaluta",
      voucher: "Voucher",
    },
    en: {
      mobile: "Mobile payments",
      bank: "Bank transfer",
      card: "Cards",
      ewallet: "E-wallet",
      crypto: "Crypto",
      voucher: "Voucher",
    },
  } as const;
  const groupLabels = isSv ? groups.sv : groups.en;
  const grouped = paymentMethods
    .filter((p) => p.published)
    .reduce<Record<string, typeof paymentMethods>>((acc, m) => {
      acc[m.type] = acc[m.type] || [];
      acc[m.type].push(m);
      return acc;
    }, {});

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Hur du väljer rätt metod" : "How to pick the right method"}</h2>
        <p>
          {isSv
            ? "Tre faktorer styr valet: hur snabbt du vill ha pengarna på kontot, hur snabbt du vill ha uttagen tillbaka, och om casinot du tänker spela på har svensk licens. Hos svenska licensierade casinon är Swish, Trustly och Zimpler de tre vanligaste metoderna eftersom de stöder BankID och svenska bankkonton. Hos casinon utan svensk licens dominerar Trustly, Brite och kortbetalningar — krypto är också vanligare där."
            : "Three factors decide: how fast you want money in, how fast you want it back out, and whether the casino holds a Swedish license. At Swedish-licensed casinos Swish, Trustly and Zimpler are the three most common methods because they support BankID and Swedish bank accounts. At offshore casinos Trustly, Brite and card payments dominate — crypto is also more common there."}
        </p>
        <h2>{isSv ? "Skattefrågor" : "Tax considerations"}</h2>
        <p>
          {isSv
            ? "Vinster på svenskt licensierade casinon är skattefria för dig som privatperson. Vinster från sajter utanför EES kan vara skattepliktiga och måste deklareras. Spel inom EES är vanligtvis skattefritt — men reglerna kan ändras. Kontrollera med Skatteverket vid osäkerhet."
            : "Winnings at Swedish-licensed casinos are tax-free for you as a private individual. Winnings from sites outside the EEA can be taxable and need to be declared. Gambling inside the EEA is usually tax-free — but rules can change. Check with Skatteverket if uncertain."}
        </p>
      </article>

      <div className="mt-10 space-y-10">
        {Object.entries(grouped).map(([type, items]) => (
          <section key={type}>
            <h2 className="text-2xl font-bold">
              {groupLabels[type as keyof typeof groupLabels] ?? type}
            </h2>
            <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => (
                <Link
                  key={p.id}
                  to={paymentPath(prefix, lang, isSv ? p.slug_sv : p.slug_en)}
                  className="card-interactive p-5 no-underline"
                >
                  <h3 className="text-lg font-semibold">{p.name}</h3>
                  <p className="mt-1 text-sm text-ink-600 line-clamp-3">
                    {isSv ? p.description_sv : p.description_en}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.requires_bankid && <span className="pill-brand">BankID</span>}
                    {p.works_with_swedish_license && (
                      <span className="pill-success">{t("label.swedishLicense")}</span>
                    )}
                    {p.works_without_swedish_license && (
                      <span className="pill-warn">{t("label.noSwedishLicense")}</span>
                    )}
                    {p.type === "crypto" && (
                      <span className="pill-brand">{t("label.crypto")}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>

      <InternalLinks context="payment" />
    </div>
  );
}
